'use client';

import React from 'react';
import Link from 'next/link';
import { Calendar, Clock, User, ArrowRight, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';
import { getAllArticles, BLOG_CATEGORY_KEYS } from '@/lib/blog';
import type { BlogArticle, BlogCategoryKey } from '@/lib/blog';

// Chip styling kept as constants so the index page and the category hubs render
// byte-identical chips — the design must not drift between the two surfaces.
const CHIP_BASE = 'px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200';
const CHIP_ACTIVE = 'bg-primary-500 text-white shadow-md';
const CHIP_IDLE =
  'bg-white text-gray-600 border border-gray-200 hover:border-primary-300 hover:text-primary-600';

/**
 * Category chips.
 *
 * These used to be onClick filters, which produced zero crawlable URLs: the five
 * categories existed only in client state, so no crawler (and no AI crawler that
 * never executes JS) could reach a category listing. Each category now has a real
 * hub at /blog/category/<key> and the chips are plain anchors, so link equity
 * flows from /blog into the hubs and between hubs. `activeCategoryKey === null`
 * means we are on the /blog index, where "All" is the active chip.
 */
export function CategoryChips({
  activeCategoryKey = null,
}: {
  activeCategoryKey?: BlogCategoryKey | null;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap justify-center gap-3 mb-12">
      <Link
        href="/blog"
        className={`${CHIP_BASE} ${activeCategoryKey === null ? CHIP_ACTIVE : CHIP_IDLE}`}
        {...(activeCategoryKey === null ? { 'aria-current': 'page' as const } : {})}
      >
        {t('pageContent.blog.allCategories')}
      </Link>
      {BLOG_CATEGORY_KEYS.map((categoryKey) => (
        <Link
          key={categoryKey}
          href={`/blog/category/${categoryKey}`}
          className={`${CHIP_BASE} ${activeCategoryKey === categoryKey ? CHIP_ACTIVE : CHIP_IDLE}`}
          {...(activeCategoryKey === categoryKey ? { 'aria-current': 'page' as const } : {})}
        >
          {t(`pageContent.blog.categories.${categoryKey}`)}
        </Link>
      ))}
    </div>
  );
}

/**
 * A single article card. Exported so the category hubs reuse the exact markup
 * from the index instead of a second, drifting copy of the design.
 */
export function ArticleCard({
  article,
  index,
  isVisible,
}: {
  article: BlogArticle;
  index: number;
  isVisible: boolean;
}) {
  const { t, i18n } = useTranslation();

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Link
      href={`/blog/${article.slug}`}
      className={`group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow hover-lift fade-in-section ${isVisible ? 'is-visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="p-6">
        {/* Category Badge */}
        <div className="mb-4">
          <span className="inline-flex items-center gap-1 bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-semibold">
            <Tag className="h-3 w-3" />
            {article.categoryKey
              ? t(`pageContent.blog.categories.${article.categoryKey}`, { defaultValue: article.category })
              : article.category}
          </span>
        </div>

        {/*
          h2, not h3: the index page has no section heading between the h1 and the
          cards, so h3 left a level-skip (h1 -> h3) and no h2 anywhere in the DOM.
          Each card title is a top-level item under the h1, and promoting them keeps
          the outline contiguous using the real, already-translated article titles
          instead of a generic section label. If a labelled heading is later added
          above the grid (needs a new i18n key — see report), demote these back to h3.
        */}
        <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
          {t(`blogArticles.${article.slug}.title`, { defaultValue: article.title })}
        </h2>

        {/* Excerpt */}
        <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
          {t(`blogArticles.${article.slug}.excerpt`, { defaultValue: article.excerpt })}
        </p>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-4">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatDate(article.date)}
          </span>
          <span className="inline-flex items-center gap-1">
            <User className="h-3.5 w-3.5" />
            {article.author}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {t(`blogArticles.${article.slug}.readTime`, { defaultValue: article.readTime })}
          </span>
        </div>

        {/* Read More */}
        <span className="inline-flex items-center text-primary-500 font-medium text-sm group-hover:text-primary-600 transition-colors">
          {t('pageContent.blog.readMore')}
          <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export default function BlogContent() {
  const { t } = useTranslation();

  const { ref: heroRef, isVisible: heroVisible } = useIntersectionObserver();
  const { ref: gridRef, isVisible: gridVisible } = useIntersectionObserver();

  // No client-side filtering any more: filtering lives on the crawlable
  // /blog/category/<key> hubs, and /blog is the "All" view.
  const allArticles = getAllArticles();

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container">
          <div ref={heroRef} className={`max-w-3xl mx-auto text-center fade-in-section ${heroVisible ? 'is-visible' : ''}`}>
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.blog.hero.tagline')}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('pageContent.blog.hero.title')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('pageContent.blog.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter & Articles */}
      <section className="py-16 md:py-20">
        <div className="container">
          {/* Featured report. /reports/state-of-early-stage-vc-2026 was in the
              sitemap and llms-full.txt but had zero internal inlinks, so Ahrefs
              flagged it as an orphan page. A data study is the page type most
              likely to earn external citations — but only if it is reachable. */}
          <Link
            href="/reports/state-of-early-stage-vc-2026"
            className="group block max-w-3xl mx-auto mb-12 p-6 md:p-8 rounded-2xl border border-gray-200 bg-white hover:border-primary-300 hover:shadow-md transition-all duration-200"
          >
            <span className="inline-block bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-semibold mb-3">
              {t('pageContent.blog.featuredReport.tagline')}
            </span>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              {t('pageContent.blog.featuredReport.title')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('pageContent.blog.featuredReport.description')}
            </p>
            <span className="inline-flex items-center text-primary-500 font-medium text-sm group-hover:text-primary-600 transition-colors">
              {t('pageContent.blog.featuredReport.cta')}
              <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </Link>

          {/* Category Filters */}
          <CategoryChips activeCategoryKey={null} />

          {/* Articles Grid */}
          <div ref={gridRef}>
            {allArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {allArticles.map((article: BlogArticle, index: number) => (
                  <ArticleCard
                    key={article.slug}
                    article={article}
                    index={index}
                    isVisible={gridVisible}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-xl text-gray-500">{t('pageContent.blog.noPosts')}</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
